import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import React, { FC, memo, useCallback, useRef, useState, useEffect } from 'react';
import { SizeCodeForBooking } from 'entities/SizeCode';
import { getSelectedFilter } from 'features/BoxSizeFilter/model/selectors/getSelectedFilter';
import { boxSizeFilterActions } from 'features/BoxSizeFilter/model/slice/boxSizeFilterSlice';
import { SizeFilter } from 'features/BoxSizeFilter/model/types';
import { useModernSizeGroupsFilters } from '../utils/hooks/useModernSizeGroupsFilters';
import { ModernFilterOption } from './ModernFilterOption';

interface ModernBoxSizeFilterMobileProps {
  sizeCodes: SizeCodeForBooking[] | undefined;
}

export const ModernBoxSizeFilterMobile: FC<ModernBoxSizeFilterMobileProps> = memo((props) => {
  const { sizeCodes } = props;

  const dispatch = useAppDispatch();
  const selectedFilter = useAppSelector(getSelectedFilter);
  const sizesFilters = useModernSizeGroupsFilters(sizeCodes);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const changeFilter = useCallback(
    (newFilter: SizeFilter): void => {
      console.log('newFilter', newFilter);
      dispatch(boxSizeFilterActions.changeFilter(newFilter));
    },
    [dispatch],
  );

  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding issues
    }
  }, []);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    checkScrollButtons();
    const handleResize = (): void => {
      checkScrollButtons();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [checkScrollButtons, sizesFilters]);

  const getSizeCode = (value: SizeFilter): string | undefined => {
    if (value === 'All') return undefined;
    return value;
  };

  if (!sizesFilters.length) {
    return null;
  }

  return (
    <div className="bg-white w-full">
      <div className="relative w-full">
        {/* Left scroll button */}
        {canScrollLeft && (
          <button
            onClick={scrollLeft}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <span className="text-gray-600 font-bold">‹</span>
          </button>
        )}

        {/* Filter options container */}
        <div ref={scrollContainerRef} className="flex gap-3 overflow-x-auto hide-scrollbar w-full" onScroll={checkScrollButtons}>
          {sizesFilters.map(({ label, description, value, isDisabled }) => (
            <ModernFilterOption
              key={value}
              label={label}
              description={description}
              value={value}
              isActive={selectedFilter === value}
              isDisabled={isDisabled}
              onChange={changeFilter}
              sizeCode={getSizeCode(value)}
            />
          ))}
        </div>

        {/* Right scroll button */}
        {canScrollRight && (
          <button
            onClick={scrollRight}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <span className="text-gray-600 font-bold">›</span>
          </button>
        )}
      </div>
    </div>
  );
});
